import React from 'react';
import styled from 'styled-components';
import { useDrag } from 'react-use-gesture';
import useIsRtl from 'hooks/useIsRtl';

const SwiperViewport = styled.div`
  overflow: hidden;
  touch-action: pan-y;
`;

const transformPosition = (index: number, isRtl: boolean) =>
  isRtl ? `translateX(${index * 100}%)` : `translateX(-${index * 100}%)`;

const SwiperScroll = styled.div<{ currentIndex: number; isRtl: boolean }>`
  flex-grow: 1;
  display: flex;
  max-width: 100%;
  max-height: 100%;
  flex-shrink: 1;
  will-change: transform;
  transition: transform 600ms cubic-bezier(0.77, 0, 0.175, 1);
  transform: ${props => transformPosition(props.currentIndex, props.isRtl)};
`;

export const SwiperItem = styled.div`
  width: 100%;
  max-width: 100%;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  pointer-events: none;
  user-select: none;
  overflow: hidden;
`;

interface SwiperProps {
  children: React.ReactNode;
  currentIndex: number;
  onSwipe?: (direction: number) => void;
}

const V_THRESHOLD = 0.1;

const Swiper = React.forwardRef<HTMLDivElement, SwiperProps>(
  ({ children, currentIndex, onSwipe }, ref) => {
    const isRtl = useIsRtl();
    const bind = useDrag(({ last, vxvy: [vx] }) => {
      if (last && onSwipe) {
        // swipe left is when horizontal velocity is inferior to minus threshold
        if (vx < -V_THRESHOLD) {
          onSwipe(1);
        }
        // swipe right is when horizontal velocity is superior to threshold
        else if (vx > V_THRESHOLD) {
          onSwipe(-1);
        }
      }
    });

    return (
      <SwiperViewport {...bind()} ref={ref}>
        <SwiperScroll currentIndex={currentIndex} isRtl={isRtl}>
          {children}
        </SwiperScroll>
      </SwiperViewport>
    );
  }
);

export default Swiper;
